import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  useDeferredValue,
  useRef,
} from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button as MuiButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
  FileUploadOutlined,
} from "@mui/icons-material";
import { toast } from "react-hot-toast";
import { useAuth } from "../../contexts/auth";
import { baseAxios } from "../../utils/config";
import {
  PaginatedTable,
  CreateServiceForm,
  EditServiceForm,
} from "../../components";
import ImportServicesDialog from "./ImportServicesDialog";
import { colors } from "..";

const STATUS_CHIP_COLOR = {
  Active: "success",
  Inactive: "default",
};

const MARK_STYLE = {
  backgroundColor: "#fff882",
  color: "inherit",
  borderRadius: 4,
};

function HeaderColumnFilter({
  ariaLabel,
  value,
  onChange,
  anchorEl,
  setAnchorEl,
  options,
  allMenuLabel,
}) {
  const active = value !== "" && value != null;
  return (
    <Box
      component="span"
      onMouseDown={(e) => e.stopPropagation()}
      sx={{ display: "inline-flex", alignItems: "center", ml: 0.25 }}
    >
      <IconButton
        size="small"
        aria-label={ariaLabel}
        aria-haspopup="true"
        aria-expanded={Boolean(anchorEl)}
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl((prev) =>
            prev === e.currentTarget ? null : e.currentTarget
          );
        }}
        color={active ? "primary" : "default"}
        sx={{
          p: 0.35,
          color: active ? "primary.main" : "rgba(255, 255, 255, 0.85)",
          opacity: active ? 1 : 0.85,
          "&:hover": {
            color: "common.white",
            backgroundColor: "rgba(255, 255, 255, 0.08)",
          },
        }}
      >
        <FilterListIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: { maxWidth: 360, mt: 0.5 },
          },
        }}
      >
        <MenuItem
          selected={value === ""}
          onClick={() => {
            onChange("");
            setAnchorEl(null);
          }}
        >
          {allMenuLabel}
        </MenuItem>
        {options.map((opt) => (
          <MenuItem
            key={String(opt.value)}
            selected={value === opt.value}
            onClick={() => {
              onChange(opt.value);
              setAnchorEl(null);
            }}
            sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

function HighlightText({ text, query }) {
  const str = String(text ?? "");
  const q = String(query ?? "").trim();
  if (!q || !str) return str || null;

  const tokens = q
    .split(/\s+/)
    .filter(Boolean)
    .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  if (!tokens.length) return str;

  let pattern;
  try {
    pattern = new RegExp(`(${tokens.join("|")})`, "gi");
  } catch {
    return str;
  }

  const parts = str.split(pattern);
  if (parts.length === 1) return str;

  return parts.map((part, i) =>
    pattern.test(part) ? (
      <mark key={i} style={MARK_STYLE}>
        {part}
      </mark>
    ) : (
      part
    )
  );
}

function serviceMatchesSearch(row, rawQuery) {
  const q = String(rawQuery ?? "").trim().toLowerCase();
  if (!q) return true;

  const blob = [
    row.service_name,
    row.service_shortcut,
    row.description,
  ]
    .map((x) => String(x ?? "").toLowerCase())
    .join(" ");

  const tokens = q.split(/\s+/).filter(Boolean);
  return tokens.every((tok) => blob.includes(tok));
}

function compareServiceRows(a, b, field) {
  const va = a[field];
  const vb = b[field];
  const sa = va == null ? "" : String(va);
  const sb = vb == null ? "" : String(vb);
  const primary = sa.localeCompare(sb, undefined, { sensitivity: "base" });
  if (primary !== 0) return primary;
  return (Number(a.service_id) || 0) - (Number(b.service_id) || 0);
}

export default function SearchServices() {
  const { user } = useAuth();

  const searchServiceAPi = "gateway/ccme69ih27";
  const getOrganizationApi = "gateway/wankbr6e73";

  const userCredentials = useMemo(
    () => ({
      Barcode: user.barcode,
      Role: user.role,
      Organization: user.org,
      Location: user.location_id,
    }),
    [user.barcode, user.role, user.org, user.location_id]
  );

  const [organizationName, setOrganizationName] = useState("");
  const [services, setServices] = useState([]);
  const loadRequestRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const deferredSearch = useDeferredValue(searchInput);
  const [filterStatus, setFilterStatus] = useState("");
  const [statusFilterMenuAnchor, setStatusFilterMenuAnchor] = useState(null);
  const [sortBy, setSortBy] = useState("service_name");
  const [sortOrder, setSortOrder] = useState("asc");

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [dialogServiceId, setDialogServiceId] = useState(null);
  const [createSaving, setCreateSaving] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  const openCreateService = useCallback(() => {
    setCreateDialogOpen(true);
  }, []);

  const closeCreateDialog = useCallback(() => {
    setCreateDialogOpen(false);
  }, []);

  const openEditService = useCallback((serviceId) => {
    setDialogServiceId(serviceId);
    setEditDialogOpen(true);
  }, []);

  const closeEditDialog = useCallback(() => {
    setEditDialogOpen(false);
  }, []);

  const handleEditDialogExited = useCallback(() => {
    setDialogServiceId(null);
  }, []);

  useEffect(() => {
    async function getOrganization() {
      try {
        const res = await baseAxios.post(getOrganizationApi, userCredentials);
        const arrayData = res.data;
        if (!Array.isArray(arrayData)) return;

        const matchingOrg = arrayData.find((org) => {
          const parts = org.split(" ");
          return parts[0] === userCredentials.Organization.toString();
        });

        if (matchingOrg) {
          const parts = matchingOrg.split(" ");
          setOrganizationName(parts.slice(1).join(" "));
        }
      } catch {
        setOrganizationName("");
      }
    }
    getOrganization();
  }, [userCredentials]);

  const loadServices = useCallback(() => {
    const requestId = ++loadRequestRef.current;
    setLoading(true);
    baseAxios
      .post(searchServiceAPi, userCredentials)
      .then((result) => {
        if (requestId !== loadRequestRef.current) return;
        const data = result.data;
        setServices(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (requestId !== loadRequestRef.current) return;
        setServices([]);
        toast.error("Could not load services.", { duration: 3000 });
      })
      .finally(() => {
        if (requestId !== loadRequestRef.current) return;
        setLoading(false);
      });
  }, [searchServiceAPi, userCredentials]);

  const servicesWithOrganization = useMemo(
    () =>
      services.map((item) => ({
        ...item,
        organizationName,
      })),
    [services, organizationName]
  );

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  const onServiceSaved = useCallback(() => {
    loadServices();
    closeCreateDialog();
    closeEditDialog();
  }, [loadServices, closeCreateDialog, closeEditDialog]);

  const statusFilterOptions = useMemo(() => {
    const set = new Set();
    for (const row of servicesWithOrganization) {
      if (row.service_status) set.add(row.service_status);
    }
    return [...set].sort((a, b) =>
      String(a).localeCompare(String(b), undefined, { sensitivity: "base" })
    );
  }, [servicesWithOrganization]);

  const filteredServices = useMemo(() => {
    return servicesWithOrganization.filter((row) => {
      if (!serviceMatchesSearch(row, deferredSearch)) return false;
      if (filterStatus && row.service_status !== filterStatus) return false;
      return true;
    });
  }, [servicesWithOrganization, deferredSearch, filterStatus]);

  const sortedServices = useMemo(() => {
    const copy = [...filteredServices];
    copy.sort((a, b) => {
      const field = sortBy === "organizationName" ? "organizationName" : sortBy;
      const c = compareServiceRows(a, b, field);
      return sortOrder === "asc" ? c : -c;
    });
    return copy;
  }, [filteredServices, sortBy, sortOrder]);

  const handleSortChange = useCallback((field, order) => {
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const noopPagination = useCallback(() => {}, []);

  const muiColumns = useMemo(() => {
    const sq = deferredSearch;
    return [
      {
        id: "organizationName",
        label: "Organization Name",
        field: "organizationName",
        minWidth: 180,
        render: (value) =>
          value ? <HighlightText text={value} query={sq} /> : "-",
      },
      {
        id: "service_name",
        label: "Service Name",
        field: "service_name",
        minWidth: 180,
        render: (value) =>
          value ? <HighlightText text={value} query={sq} /> : "-",
      },
      {
        id: "service_shortcut",
        label: "Service Shortcut",
        field: "service_shortcut",
        minWidth: 140,
        render: (value) =>
          value ? <HighlightText text={value} query={sq} /> : "-",
      },
      {
        id: "description",
        label: "Description",
        field: "description",
        minWidth: 220,
        render: (value) =>
          value ? <HighlightText text={value} query={sq} /> : "-",
      },
      {
        id: "service_status",
        label: "Service Status",
        field: "service_status",
        minWidth: 132,
        headerExtras: (
          <HeaderColumnFilter
            ariaLabel="Filter by status"
            value={filterStatus}
            onChange={setFilterStatus}
            anchorEl={statusFilterMenuAnchor}
            setAnchorEl={setStatusFilterMenuAnchor}
            allMenuLabel="All statuses"
            options={statusFilterOptions.map((s) => ({
              value: s,
              label: s,
            }))}
          />
        ),
        render: (value) => {
          if (!value) return "-";
          return (
            <Chip
              size="small"
              variant="filled"
              label={value}
              color={STATUS_CHIP_COLOR[value] ?? "default"}
              sx={{ fontWeight: 500, lineHeight: 1 }}
            />
          );
        },
      },
      {
        id: "actions",
        label: "Actions",
        sortable: false,
        render: (_, row) => (
          <MuiButton
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<EditIcon sx={{ fontSize: 14 }} />}
            onClick={() => openEditService(row.service_id)}
            sx={{
              minHeight: 24,
              py: 0.25,
              px: 1,
              textTransform: "none",
              fontSize: "0.75rem",
              color: (theme) =>
                theme.palette.mode === "dark"
                  ? theme.palette.text.primary
                  : colors.black,
              "& .MuiButton-startIcon": { mr: 0.5, color: "primary.main" },
            }}
          >
            Edit
          </MuiButton>
        ),
      },
    ];
  }, [
    deferredSearch,
    filterStatus,
    statusFilterMenuAnchor,
    statusFilterOptions,
    openEditService,
  ]);

  const dialogPaperProps = {
    sx: {
      borderRadius: 2,
      maxHeight: "min(92vh, 900px)",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
  };

  const renderServiceDialog = ({
    open,
    onClose,
    title,
    subtitle,
    formId,
    saving,
    submitLabel,
    onExited,
    children,
  }) => (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      TransitionProps={onExited ? { onExited } : undefined}
      PaperProps={dialogPaperProps}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          bgcolor: "secondary.main",
          color: "common.white",
          borderBottom: "1px solid",
          borderColor: "primary.main",
          px: 3,
          py: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="inherit">
            {title}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "rgba(255, 255, 255, 0.75)", mt: 0.35 }}
          >
            {subtitle}
          </Typography>
        </Box>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          disabled={saving}
          size="small"
          sx={{
            color: "primary.main",
            mt: 0.25,
            "&:hover": { bgcolor: "rgba(197, 160, 111, 0.16)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 0,
          flex: 1,
          minHeight: 0,
          overflow: "auto",
        }}
      >
        <Box sx={{ p: 3 }}>{children}</Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          gap: 1,
        }}
      >
        <MuiButton
          onClick={onClose}
          disabled={saving}
          variant="outlined"
          color="secondary"
          sx={{ textTransform: "none", minWidth: 100 }}
        >
          Cancel
        </MuiButton>
        <MuiButton
          type="submit"
          form={formId}
          variant="contained"
          color="primary"
          disabled={saving}
          sx={{
            minWidth: 140,
            textTransform: "none",
            fontWeight: 700,
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          }}
        >
          {saving ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            submitLabel
          )}
        </MuiButton>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box sx={{ px: 8, py: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mt: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography fontWeight={600} sx={{ fontSize: "1.5rem" }}>
            Manage Services
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View, search, and manage services for your organization.
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder="Search name, shortcut, or description…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            variant="outlined"
            sx={{
              minWidth: 320,
              maxWidth: "100%",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "background.paper",
                borderRadius: "6px",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 0.75 }}>
                  <SearchIcon
                    sx={{
                      fontSize: 20,
                      color: "grey.400",
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end" sx={{ mr: 0.75 }}>
                  <IconButton
                    size="small"
                    onClick={() => setSearchInput("")}
                    edge="end"
                    aria-label="Clear search"
                    sx={{
                      color: "grey.500",
                      "&:hover": {
                        color: "grey.800",
                        backgroundColor: "grey.200",
                      },
                    }}
                  >
                    <ClearIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <IconButton
            aria-label="Refresh services"
            onClick={loadServices}
            disabled={loading}
            size="medium"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              borderRadius: "6px",
              "&:hover": { bgcolor: "grey.100" },
            }}
          >
            <RefreshIcon />
          </IconButton>
          <MuiButton
            variant="outlined"
            color="primary"
            onClick={() => setImportDialogOpen(true)}
            startIcon={<FileUploadOutlined />}
            sx={{
              minHeight: 40,
              px: 2,
              textTransform: "none",
              fontWeight: 700,
              bgcolor: "background.paper",
            }}
          >
            Import Services
          </MuiButton>
          <MuiButton
            variant="contained"
            color="primary"
            onClick={openCreateService}
            sx={{
              minHeight: 40,
              px: 2,
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            <AddIcon sx={{ mr: 0.75 }} /> Add Service
          </MuiButton>
        </Box>
      </Box>

      <PaginatedTable
        columns={muiColumns}
        rows={sortedServices}
        totalRowCount={sortedServices.length}
        loading={loading}
        onPaginationChange={noopPagination}
        onSortChange={handleSortChange}
        initialOrderBy="service_name"
        initialOrder="asc"
        searchQuery={`${deferredSearch}|${filterStatus}`}
        clientSidePagination
        skeletonHeight="40px"
        getRowKey={(row) => row.service_id}
        emptyMessage={
          String(deferredSearch ?? "").trim() || filterStatus
            ? "No services match your search"
            : "No data to display"
        }
      />

      {renderServiceDialog({
        open: createDialogOpen,
        onClose: closeCreateDialog,
        title: "Add Service",
        subtitle: "Create a new service with details, category, and pricing.",
        formId: "create-service-form",
        saving: createSaving,
        submitLabel: "Create Service",
        children: (
          <CreateServiceForm
            key="new-service"
            formId="create-service-form"
            onSaved={onServiceSaved}
            onRequestClose={closeCreateDialog}
            onLoadingChange={setCreateSaving}
          />
        ),
      })}

      {renderServiceDialog({
        open: editDialogOpen,
        onClose: closeEditDialog,
        title: "Edit Service",
        subtitle: "Update service details, category, and pricing levels.",
        formId: "edit-service-form",
        saving: editSaving,
        submitLabel: "Save Changes",
        onExited: handleEditDialogExited,
        children:
          dialogServiceId != null ? (
            <EditServiceForm
              key={`service-${dialogServiceId}`}
              formId="edit-service-form"
              serviceId={dialogServiceId}
              onSaved={onServiceSaved}
              onRequestClose={closeEditDialog}
              onLoadingChange={setEditSaving}
            />
          ) : null,
      })}

      <ImportServicesDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImportComplete={loadServices}
      />
    </Box>
  );
}
